/**
 * AIDealsPage — /v3/crm/ai-deals
 *
 * Ultra-premium tier-gated feature. The page renders three different
 * states based on the operator's entitlement:
 *
 *   1. **Locked** (Starter / Basic) → gorgeous upgrade page with the
 *      autonomous-Jarvis pitch, feature list, and an Upgrade CTA
 *   2. **Unlocked, Phase 1 not yet released** (Pro / Team) → "🚀 Early
 *      Access — you're first in line" waitlist card. Meter chip shows
 *      their monthly cap so they know what they're getting.
 *   3. **Unlocked, engine live** — future: the actual autonomous SDR
 *      dashboard (this state doesn't render until Phase 1 ships)
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Loader2, Sparkles, Rocket, Lock, Check, ArrowRight, Zap, Phone,
  MessageSquare, CalendarClock, Shield, Crown,
} from "lucide-react";

export default function AIDealsPage() {
  const [ent, setEnt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/v3/ai-sdr/entitlement");
        if (!cancel) setEnt(data);
      } catch { /* fall through to locked state */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="ai-deals-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!ent?.enabled) return <LockedState ent={ent} />;
  return <EarlyAccessState ent={ent} />;
}

// ─────────────────────────────────────────── locked ─────────────────────
function LockedState({ ent }) {
  const currentTier = ent?.tier || "starter";
  return (
    <div className="space-y-6" data-testid="ai-deals-locked">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-black text-amber-600">
          Ultra Premium · Pro & Team
        </div>
        <h1 className="mt-1 text-[30px] font-bold text-slate-900 tracking-tight leading-tight">
          Autonomous Jarvis
        </h1>
        <p className="mt-1.5 text-slate-600 text-[13.5px] max-w-2xl">
          Your AI operating partner chats up sellers, follows up, and books the appointment —
          while you&rsquo;re out being a human.
        </p>
      </div>

      {/* Hero card */}
      <div
        className="relative overflow-hidden rounded-2xl border border-amber-200 shadow-lg"
        style={{ backgroundImage: "linear-gradient(135deg, #FEFCF8 0%, #FEF3C7 60%, #FDE68A 100%)" }}
      >
        <div className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full bg-white/70 backdrop-blur border border-amber-200 px-2 py-0.5 text-[9.5px] font-black uppercase tracking-widest text-amber-800">
          <Lock className="w-3 h-3" /> Locked on {currentTier}
        </div>
        <div className="p-6 md:p-10 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white border border-amber-200 px-2.5 py-1 text-[10.5px] font-black uppercase tracking-widest text-amber-800">
              <Sparkles className="w-3 h-3" /> Beta
            </div>
            <h2 className="mt-3 text-[24px] md:text-[30px] font-bold text-slate-900 leading-tight">
              Sleep on it. Wake up to booked calls.
            </h2>
            <p className="mt-3 text-slate-700 text-[14px] max-w-md">
              Every seller reply, follow-up nudge, and appointment offer runs on autopilot until
              Jarvis flags one worth your voice. You take over with a single tap.
            </p>
            <Link
              to="/v3/billing"
              data-testid="ai-deals-upgrade-cta"
              className="mt-5 inline-flex items-center gap-1.5 rounded-lg text-white text-[13.5px] font-bold px-4 py-2.5 shadow-md hover:shadow-lg transition-all"
              style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}
            >
              Upgrade to Pro <ArrowRight className="w-4 h-4" />
            </Link>
            <div className="mt-2 text-[11px] text-slate-500">
              200 auto-replies included per month · overage on credits
            </div>
          </div>

          {/* Feature grid */}
          <ul className="grid grid-cols-1 gap-2.5">
            {[
              { icon: MessageSquare, label: "Auto-replies to every seller SMS in your voice" },
              { icon: CalendarClock, label: "Books appointments on your calendar without touching a screen" },
              { icon: Phone,         label: "Escalates hot leads to your phone the instant they warm up" },
              { icon: Shield,        label: "TCPA-safe · STOP-aware · quiet hours enforced" },
              { icon: Crown,         label: "Founder cohort: Team volume at Pro price for life" },
            ].map(({ icon: Icon, label }, i) => (
              <li key={i} className="flex items-start gap-2.5 rounded-lg bg-white/70 backdrop-blur border border-amber-200 px-3 py-2.5">
                <Icon className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
                <span className="text-[12.5px] text-slate-800 font-medium">{label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tier comparison */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 text-[10px] uppercase tracking-widest font-black text-amber-700">
          What each tier unlocks
        </div>
        <table className="w-full text-[13px]">
          <thead className="text-[10.5px] uppercase tracking-widest font-black text-slate-500 bg-slate-50/60">
            <tr>
              <th className="text-left px-4 py-2.5">Tier</th>
              <th className="text-left px-4 py-2.5">Access</th>
              <th className="text-right px-4 py-2.5">Included</th>
              <th className="text-right px-4 py-2.5">Overage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr className={currentTier === "starter" ? "bg-amber-50/50" : ""}>
              <td className="px-4 py-2.5 font-semibold text-slate-800">Starter</td>
              <td className="px-4 py-2.5 text-rose-600 font-semibold">Locked</td>
              <td className="px-4 py-2.5 text-right text-slate-400">—</td>
              <td className="px-4 py-2.5 text-right text-slate-400">—</td>
            </tr>
            <tr className={currentTier === "basic" ? "bg-amber-50/50" : ""}>
              <td className="px-4 py-2.5 font-semibold text-slate-800">Basic</td>
              <td className="px-4 py-2.5 text-rose-600 font-semibold">Locked</td>
              <td className="px-4 py-2.5 text-right text-slate-400">—</td>
              <td className="px-4 py-2.5 text-right text-slate-400">—</td>
            </tr>
            <tr className={currentTier === "pro" ? "bg-amber-50/50 ring-1 ring-amber-200" : ""}>
              <td className="px-4 py-2.5 font-semibold text-slate-800">Pro</td>
              <td className="px-4 py-2.5 text-emerald-700 font-semibold inline-flex items-center gap-1">
                <Check className="w-3.5 h-3.5" /> Enabled
              </td>
              <td className="px-4 py-2.5 text-right text-slate-800 font-bold tabular-nums">200 / mo</td>
              <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">50 credits / thread</td>
            </tr>
            <tr className={currentTier === "team" ? "bg-amber-50/50 ring-1 ring-amber-200" : ""}>
              <td className="px-4 py-2.5 font-semibold text-slate-800">Team</td>
              <td className="px-4 py-2.5 text-emerald-700 font-semibold inline-flex items-center gap-1">
                <Check className="w-3.5 h-3.5" /> Enabled
              </td>
              <td className="px-4 py-2.5 text-right text-slate-800 font-bold tabular-nums">1,000 / mo</td>
              <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">25 credits / thread</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────── unlocked, waitlist ─────────
function EarlyAccessState({ ent }) {
  const pct = Math.min(100, ent.monthly_cap ? Math.round((ent.used / ent.monthly_cap) * 100) : 0);
  const [readiness, setReadiness] = useState(null);
  const [persona, setPersona] = useState(null);
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    const [r, p] = await Promise.all([
      api.get("/v3/ai-sdr/readiness").then((x) => x.data).catch(() => null),
      api.get("/v3/ai-sdr/persona").then((x) => x.data).catch(() => null),
    ]);
    if (r) setReadiness(r);
    if (p) { setPersona(p); setName(p.name || ""); setBrand(p.brand || ""); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const savePersona = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/v3/ai-sdr/persona", { name, brand });
      setPersona(data);
      toast.success("Persona saved");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="space-y-6" data-testid="ai-deals-early-access">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-amber-600">
            Early Access · {ent.tier}
          </div>
          <h1 className="mt-1 text-[30px] font-bold text-slate-900 tracking-tight leading-tight">
            Autonomous Jarvis
          </h1>
          <p className="mt-1.5 text-slate-600 text-[13.5px] max-w-2xl">
            Once your checklist below is green, Jarvis starts replying to sellers, following up, and
            booking appointments on your calendar — using your persona and your Twilio number.
          </p>
        </div>
        {ent.founder_perk && (
          <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-[11.5px] font-black text-amber-800">
            <Crown className="w-3.5 h-3.5" /> FOUNDER PERK · Team volume at Pro price · for life
          </div>
        )}
      </div>

      {/* Readiness checklist */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="ai-deals-readiness">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/40">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-black text-amber-700">
            <Rocket className="w-3.5 h-3.5" /> Launch Checklist
          </div>
          {readiness?.all_green && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-800 text-[10px] font-black uppercase tracking-widest px-2 py-0.5">
              <Check className="w-3 h-3" /> ALL GREEN
            </span>
          )}
        </div>
        {!readiness ? (
          <div className="p-6 text-slate-500 text-sm inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking…
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {readiness.checks.map((c) => (
              <li key={c.key} data-testid={`ai-deals-check-${c.key}`}
                  className="px-4 py-3 flex items-start gap-3">
                <span className={
                  "mt-0.5 rounded-full p-1 shrink-0 " +
                  (c.ok ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400")
                }>
                  <Check className="w-3.5 h-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-slate-900">{c.label}</div>
                  {c.detail && (
                    <div className="text-[11.5px] text-slate-500 mt-0.5">{c.detail}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Persona picker */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="ai-deals-persona">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/40">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-black text-amber-700">
            <MessageSquare className="w-3.5 h-3.5" /> AI Persona
          </div>
          <div className="mt-1 text-[11.5px] text-slate-500">
            The name + brand Jarvis uses when texting your sellers. Text-only for Phase 1.
          </div>
        </div>
        <div className="p-4 grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-[9.5px] uppercase tracking-widest font-black text-slate-500 mb-1 block">
              Assistant Name
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {["Jordan", "Alex", "Riley", "Morgan", "Sam"].map((n) => (
                <button
                  key={n} type="button"
                  onClick={() => setName(n)}
                  data-testid={`persona-name-${n}`}
                  className={
                    "rounded-full border text-[11.5px] font-semibold px-2.5 py-0.5 transition-colors " +
                    (name === n
                      ? "bg-amber-500 text-white border-amber-500"
                      : "bg-white text-slate-700 border-slate-200 hover:border-amber-300")
                  }
                >{n}</button>
              ))}
            </div>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Type any name"
                   data-testid="persona-name-input"
                   className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400" />
          </div>
          <div>
            <label className="text-[9.5px] uppercase tracking-widest font-black text-slate-500 mb-1 block">
              Brand / Company
            </label>
            <input type="text" value={brand} onChange={(e) => setBrand(e.target.value)}
                   placeholder="Perrin Properties"
                   data-testid="persona-brand-input"
                   className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400" />
          </div>
        </div>
        <div className="px-4 pb-4">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-[12.5px] text-slate-700">
            <div className="text-[9.5px] uppercase tracking-widest font-black text-slate-500 mb-1">Preview</div>
            <div className="italic">
              &ldquo;Hi, this is <b>{name || "…"}</b> with <b>{brand || "…"}</b> — quick question about your property.&rdquo;
            </div>
          </div>
        </div>
        <div className="px-4 pb-4 flex justify-end">
          <button type="button" onClick={savePersona} disabled={saving || !name || !brand}
                  data-testid="persona-save"
                  className="inline-flex items-center gap-1 rounded-lg text-white text-[12px] font-bold px-3 py-1.5 disabled:opacity-50"
                  style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save Persona
          </button>
        </div>
      </div>

      {/* Meter */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-black text-amber-700">
            <Zap className="w-3.5 h-3.5" /> This month
          </div>
          <div className="text-[11px] text-slate-500 tabular-nums">
            <span className="font-bold text-slate-900">{ent.used}</span> / {ent.monthly_cap.toLocaleString()}
            <span className="text-slate-400"> · </span>
            <span>{ent.remaining.toLocaleString()} remaining</span>
          </div>
        </div>
        <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full transition-all"
               style={{ width: `${pct}%`, backgroundImage: "linear-gradient(90deg, #F59E0B, #B45309)" }} />
        </div>
        <div className="mt-2 text-[11.5px] text-slate-500">
          Above the cap → each auto-reply thread burns {ent.overage_credits_per_thread} Universal Credits.
        </div>
      </div>
    </div>
  );
}
